import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Building2, Eye, Edit, TrendingUp, Mail, Send, CheckCircle2, RefreshCw, Trophy } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../foundation/altzero_base/base/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../foundation/altzero_base/base/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../foundation/altzero_base/base/components/ui/select";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";
import { useToast } from "../../../foundation/altzero_base/base/hooks/use-toast";
import { useUser } from "../../../foundation/altzero_base/base/contextapi/UserContext";
import ScopingAILayout from "../components/ScopingAILayout";
import { usePermissions } from "../../../foundation/altzero_base/base/contextapi/PermissionsContext";
import { getTenderById, updateTender, Tender, TenderStatus, getTenderProposalManifest, TenderProposalManifestEntry } from "../services/tenderService";
import { canAccessStep } from "../lib/tenderStepPermissions";
import TenderSitesOverview from "../components/TenderSitesOverview";
import SiteOffersView from "../components/SiteOffersView";
import { SendProposalToClientDialog } from "../components/email/SendProposalToClientDialog";
import { CommunicationHistory } from "../components/CommunicationHistory";

interface TenderDetails {
  tender: Tender;
  sites: any[];
  offers: any[];
}

type DialogView = 'sites' | 'offers';

interface TenderSite {
  id: string;
  name: string;
  nmi: string;
  addr_street?: string;
  addr_city?: string;
  addr_state?: string;
  addr_postcode?: string;
}

export default function TenderDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isLoading: userIsLoading } = useUser();
  const { can } = usePermissions();

  const [tenderDetails, setTenderDetails] = useState<TenderDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogView, setDialogView] = useState<DialogView>('sites');
  const [selectedSite, setSelectedSite] = useState<TenderSite | null>(null);
  const [isSendProposalOpen, setIsSendProposalOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showStepPicker, setShowStepPicker] = useState(false);
  const [manifestEntries, setManifestEntries] = useState<TenderProposalManifestEntry[]>([]);
  const [showDocPicker, setShowDocPicker] = useState(false);

  const ALL_EDIT_STEPS = [
    { step: 1, label: "Client Info", description: "Edit client details & sites" },
    { step: 2, label: "Retailer Communication", description: "Review retailer emails sent" },
    { step: 3, label: "Email Processing", description: "Re-process retailer replies" },
    { step: 4, label: "Offers Entry", description: "Add or edit retailer offers" },
    { step: 5, label: "Compare & Select", description: "Compare offers and select" },
    // Recommended Offer was step 6 and is gone — it only re-displayed Compare & Select's picks.
    // Template, Offer Details and Generate moved down one with it.
    { step: 6, label: "Template Selection", description: "Change proposal template" },
    { step: 7, label: "Offer Details", description: "Edit offer detail settings" },
    { step: 8, label: "Generate Proposal", description: "Go straight to regenerate" },
  ];

  // Only offer the steps this role may actually open — jumping to a denied step would land the
  // wizard on a locked step and bounce the user somewhere they did not ask for.
  const EDIT_STEPS = ALL_EDIT_STEPS.filter(({ step }) => canAccessStep(can, step));

  const handleEditAndRegenerate = (step: number) => {
    setShowStepPicker(false);
    navigate(`/tender/documents/new?tender=${id}&editMode=true&step=${step}`);
  };

  useEffect(() => {
    if (id) {
      loadTenderDetails(id);
    }
  }, [id]);

  const loadTenderDetails = async (tenderId: string) => {
    setLoading(true);
    try {
      const tenderResult = await getTenderById(tenderId);
      if (!tenderResult.success || !tenderResult.data) {
        throw new Error(tenderResult.error || "Failed to load tender");
      }

      const { data: sites, error: sitesError } = await supabase
        .from("tender_site")
        .select(`
          *,
          site:site_id (
            document_file_paths,
            retailer_notes
          )
        `)
        .eq("tender_id", tenderId)
        .order("created_at", { ascending: true });

      if (sitesError) throw sitesError;

      const { data: offers, error: offersError } = await supabase
        .from("offer")
        .select(`
          *,
          retailer:retailer_id (
            id,
            name,
            logo
          )
        `)
        .eq("tender_id", tenderId)
        .order("created_at", { ascending: true });

      if (offersError) throw offersError;

      const mappedSites = (sites || []).map((site: any) => ({
        ...site,
        document_file_paths: site.site?.document_file_paths || null,
        retailer_notes: site.site?.retailer_notes || null,
      }));

      setTenderDetails({
        tender: tenderResult.data,
        sites: mappedSites,
        offers: offers || [],
      });

      // Discover every generated document for this tender (Separate mode can produce
      // several) — tender.file_path alone only tracks one.
      const manifestResult = await getTenderProposalManifest(tenderId);
      if (manifestResult.success) setManifestEntries(manifestResult.data);
    } catch (error: any) {
      console.error("Error loading tender details:", error);
      toast({
        title: "Error",
        description: "Failed to load tender details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const STATUS_LABELS: Record<TenderStatus, string> = {
    draft: 'Draft',
    waiting_for_retailers_reply: 'Awaiting Replies',
    retailers_replied: 'Retailers Replied',
    under_review: 'Under Review',
    tender_ready: 'Tender Ready',
    sent_to_client: 'Send to Client',
    won: 'Won',
    lost: 'Loss',
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!id || !tenderDetails) return;
    const status = newStatus as TenderStatus;
    setUpdatingStatus(true);
    try {
      const result = await updateTender(id, { status });
      if (result.success) {
        setTenderDetails(prev => prev ? { ...prev, tender: { ...prev.tender, status } } : prev);
        toast({ title: "Status updated", description: `Status changed to ${STATUS_LABELS[status]}` });
      } else {
        toast({ title: "Error", description: result.error || "Failed to update status", variant: "destructive" });
      }
    } catch {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleViewOffers = (site: TenderSite) => {
    setSelectedSite(site);
    setDialogView('offers');
  };

  const handleBackToSites = () => {
    setSelectedSite(null);
    setDialogView('sites');
  };

  const handleViewProposal = () => {
    if (!id) return;

    if (manifestEntries.length > 1) {
      setShowDocPicker(true);
      return;
    }

    const filePath = manifestEntries[0]?.filePath || tenderDetails?.tender.file_path;
    if (filePath) {
      navigate(`/tender/proposal-viewer?tender=${id}&file=${encodeURIComponent(filePath)}&return=details`);
    }
  };

  const handleViewProposalDocument = (entry: TenderProposalManifestEntry) => {
    if (!id) return;
    setShowDocPicker(false);
    const siteParam = entry.siteId ? `&site=${entry.siteId}` : '';
    navigate(`/tender/proposal-viewer?tender=${id}&file=${encodeURIComponent(entry.filePath)}${siteParam}&return=details`);
  };

  const handleEditProposal = () => {
    if (id) {
      navigate(`/tender/proposal/${id}`);
    }
  };

  const handleProposalSent = () => {
    toast({
      title: "Proposal sent!",
      description: `The proposal has been emailed to ${tenderDetails?.tender.client?.name || "the client"}.`,
    });
    // Reload tender to reflect updated proposal_sent_at + status
    if (id) loadTenderDetails(id);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string }> = {
      draft:                       { color: "bg-gray-100 text-gray-700",    label: "Draft" },
      waiting_for_retailers_reply: { color: "bg-yellow-100 text-yellow-800", label: "Awaiting Replies" },
      retailers_replied:           { color: "bg-teal-100 text-teal-800",    label: "Retailers Replied" },
      under_review:                { color: "bg-orange-100 text-orange-800", label: "Under Review" },
      tender_ready:                { color: "bg-indigo-100 text-indigo-800", label: "Tender Ready" },
      sent_to_client:              { color: "bg-blue-100 text-blue-800",    label: "Send to Client" },
      won:                         { color: "bg-green-100 text-green-800",  label: "Won" },
      lost:                        { color: "bg-red-100 text-red-800",      label: "Loss" },
    };

    const cfg = statusConfig[status] ?? { color: "bg-gray-100 text-gray-700", label: status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
        {cfg.label}
      </span>
    );
  };

  if (userIsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const tender = tenderDetails?.tender;
  const hasProposal = Boolean(tender?.file_path) || manifestEntries.length > 0;

  return (
    <ScopingAILayout>
      <div className="w-full py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-6 sm:mb-8">
          <Link
            to="/tender/proposals"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Tenders
          </Link>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <div className="text-muted-foreground mt-2">Loading tender details...</div>
            </div>
          ) : tenderDetails ? (
            <div className="space-y-6">

              {/* ── Tender Info Header ─────────────────────────────────── */}
              <Card>
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="flex items-start gap-3 md:gap-4 flex-1 min-w-0">
                      <div className="bg-primary/10 p-2 md:p-3 rounded-lg flex-shrink-0">
                        <FileText className="h-5 w-5 md:h-6 md:w-6 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <CardTitle className="text-2xl md:text-3xl">
                          {tender?.opportunity_name || "Unnamed Tender"}
                        </CardTitle>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mt-2">
                          <div className="flex items-center gap-1.5">
                            <Building2 className="h-4 w-4" />
                            {tender?.client?.name || "—"}
                          </div>
                          <div>
                            {can('tender.edit') ? (
                              <Select
                                value={tender?.status ?? 'draft'}
                                onValueChange={handleStatusChange}
                                disabled={updatingStatus}
                              >
                                <SelectTrigger className="h-7 w-auto text-xs px-2 gap-1.5 border-dashed">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="draft">Draft</SelectItem>
                                  <SelectItem value="waiting_for_retailers_reply">Awaiting Replies</SelectItem>
                                  <SelectItem value="retailers_replied">Retailers Replied</SelectItem>
                                  <SelectItem value="under_review">Under Review</SelectItem>
                                  <SelectItem value="tender_ready">Tender Ready</SelectItem>
                                  <SelectItem value="sent_to_client">Send to Client</SelectItem>
                                  <SelectItem value="won">Won</SelectItem>
                                  <SelectItem value="lost">Loss</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              getStatusBadge(tender?.status ?? 'draft')
                            )}
                          </div>
                          <div>Created: {formatDate(tender?.created_at ?? '')}</div>
                          {/* Proposal sent badge */}
                          {tender?.proposal_sent_at && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle2 className="h-3 w-3" />
                              Proposal sent {formatDate(tender.proposal_sent_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex flex-wrap gap-2 md:flex-nowrap md:flex-shrink-0">
                      {can('tender.view') && (
                        <Button
                          onClick={handleViewProposal}
                          variant="outline"
                          className="flex items-center gap-2"
                          disabled={!hasProposal}
                        >
                          <Eye className="h-4 w-4" />
                          View Proposal
                        </Button>
                      )}
                      {can('tender.edit') && (
                        <Button
                          onClick={handleEditProposal}
                          variant="outline"
                          className="flex items-center gap-2"
                          disabled={!hasProposal}
                        >
                          <Edit className="h-4 w-4" />
                          Edit Proposal
                        </Button>
                      )}
                      {can('tender.edit') && (
                        <Button
                          onClick={() => setShowStepPicker(true)}
                          variant="outline"
                          className="flex items-center gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                        >
                          <RefreshCw className="h-4 w-4" />
                          Edit &amp; Regenerate
                        </Button>
                      )}
                      {can('tender.send_proposal') && tender?.status !== 'sent_to_client' && (
                        <Button
                          onClick={() => setIsSendProposalOpen(true)}
                          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                          disabled={!hasProposal}
                          title={!hasProposal ? "Generate a proposal first" : "Send proposal to client"}
                        >
                          <Send className="h-4 w-4" />
                          Send to Client
                        </Button>
                      )}
                      {can('tender.send_proposal') && tender?.status === 'sent_to_client' && (
                        <Button
                          onClick={() => navigate(`/tender/retailer-results/${id}`)}
                          className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white"
                          title="Notify tendered retailers of their ranking"
                        >
                          <Trophy className="h-4 w-4" />
                          Send Results to Retailers
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
              </Card>

              {/* ── Proposal sent confirmation banner ───────────────────── */}
              {tender?.proposal_sent_at && (
                <div className="flex items-center gap-3 px-5 py-3 bg-green-50 border border-green-200 rounded-xl text-green-800">
                  <Mail className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-sm">Proposal emailed to client</p>
                    <p className="text-xs text-green-700 mt-0.5">
                      Sent on {formatDate(tender.proposal_sent_at)} to {tender.client?.name || "client"}
                    </p>
                  </div>
                </div>
              )}

              {/* ── Retailer Communication Metrics ──────────────────────── */}
              {(tender?.retailers_sent_count ?? 0) > 0 && (
                <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <TrendingUp className="h-5 w-5 text-blue-600" />
                      </div>
                      Retailer Communication Metrics
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <p className="text-sm font-medium text-gray-500">Emails Sent</p>
                        <h3 className="text-3xl font-bold text-gray-900 mt-2">
                          {tender.retailers_sent_count || 0}
                        </h3>
                        <p className="text-xs text-gray-400 mt-1">To retailers</p>
                      </div>
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <p className="text-sm font-medium text-gray-500">Replies Received</p>
                        <h3 className="text-3xl font-bold text-green-600 mt-2">
                          {tender.retailers_replied_count || 0}
                        </h3>
                        <p className="text-xs text-gray-400 mt-1">From retailers</p>
                      </div>
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <p className="text-sm font-medium text-gray-500">Response Rate</p>
                        <h3 className="text-3xl font-bold text-blue-600 mt-2">
                          {(tender.retailers_sent_count ?? 0) > 0
                            ? Math.round(
                                ((tender.retailers_replied_count || 0) /
                                  (tender.retailers_sent_count ?? 1)) *
                                  100
                              )
                            : 0}%
                        </h3>
                        <p className="text-xs text-gray-400 mt-1">Reply rate</p>
                      </div>
                    </div>
                    {tender.last_email_sent_at && (
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Last email sent:</span>{" "}
                          {formatDate(tender.last_email_sent_at)}
                        </p>
                      </div>
                    )}
                    {tender.first_reply_received_at && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">First reply received:</span>{" "}
                          {formatDate(tender.first_reply_received_at)}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* ── Sites / Offers view ──────────────────────────────────── */}
              {dialogView === 'sites' ? (
                <TenderSitesOverview
                  sites={tenderDetails.sites}
                  offers={tenderDetails.offers}
                  onViewOffers={handleViewOffers}
                  tenderId={id}
                />
              ) : dialogView === 'offers' && selectedSite ? (
                <SiteOffersView
                  site={selectedSite}
                  offers={tenderDetails.offers}
                  onBack={handleBackToSites}
                />
              ) : null}

              {/* ── Communication History ───────────────────────────────────── */}
              {id && <CommunicationHistory tenderId={id} />}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-muted-foreground">Tender not found</div>
            </div>
          )}
        </div>
      </div>

      {/* ── Send Proposal to Client dialog ──────────────────────────────── */}
      {id && tender && (
        <SendProposalToClientDialog
          isOpen={isSendProposalOpen}
          onClose={() => setIsSendProposalOpen(false)}
          tenderId={id}
          tenderName={tender.opportunity_name ?? 'Tender'}
          filePath={tender.file_path ?? null}
          clientId={tender.client_id}
          clientName={tender.client?.name ?? ''}
          onSent={handleProposalSent}
        />
      )}

      {/* ── Proposal document picker (multiple docs from Separate mode) ─── */}
      <Dialog open={showDocPicker} onOpenChange={setShowDocPicker}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-primary" />
              View Proposal
            </DialogTitle>
            <DialogDescription>
              This tender has {manifestEntries.length} generated documents. Pick one to view.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {manifestEntries.map((entry) => (
              <button
                key={entry.filePath}
                onClick={() => handleViewProposalDocument(entry)}
                className="w-full flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-200 hover:border-primary/40 hover:bg-primary/5 text-left transition-colors"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {entry.mode === 'combined' ? 'Combined (all sites)' : (entry.siteName || 'Site document')}
                  </p>
                  <p className="text-xs text-gray-500">Generated {formatDate(entry.generatedAt)}</p>
                </div>
                <Eye className="h-4 w-4 text-gray-400 flex-shrink-0" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Edit & Regenerate step picker ───────────────────────────────── */}
      <Dialog open={showStepPicker} onOpenChange={setShowStepPicker}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-amber-600" />
              Edit &amp; Regenerate
            </DialogTitle>
            <DialogDescription>
              Pick a step to jump to. All steps will be unlocked so you can navigate freely, then regenerate the proposal.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {EDIT_STEPS.length === 0 && (
              <p className="text-sm text-gray-500">
                Your role has no access to any workflow step. Ask an administrator to grant the
                steps you need in Settings &gt; Permissions.
              </p>
            )}
            {EDIT_STEPS.map(({ step, label, description }) => (
              <button
                key={step}
                onClick={() => handleEditAndRegenerate(step)}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-amber-300 hover:bg-amber-50 text-left transition-colors"
              >
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-semibold text-gray-600">
                  {step}
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-900">{label}</p>
                  <p className="text-xs text-gray-500">{description}</p>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </ScopingAILayout>
  );
}
